/**
 * Theme-mode state for the console: Oxygen `light` (default — matches the design)
 * or `dark`. Persisted in localStorage so an operator's choice sticks. The chosen
 * mode is fed to `OxygenProvider`'s `themeName`, which swaps the whole Oxygen token
 * set (and therefore every component + our token-styled chrome) between the two.
 */

import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'spaces.admin.theme';

function readStored(): ThemeMode {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
    } catch {
        return 'light';
    }
}

/** Returns the current theme mode and a toggle that persists the choice. */
export function useThemeMode(): { mode: ThemeMode; toggle: () => void } {
    const [mode, setMode] = useState<ThemeMode>(readStored);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // best-effort persistence; ignore quota/availability errors
        }
    }, [mode]);

    return {
        mode,
        toggle: () => setMode(m => (m === 'light' ? 'dark' : 'light'))
    };
}
